/**
 * DossierCard — the atomic homepage card, mirrored from the design lock.
 *
 * Anatomy (mockup):
 *   • AI-generated hero image (with vignette + duotone)
 *   • Category chip (top-left) + Status chip (top-right)
 *   • Title (font-display, 2 lines max)
 *   • Intelligence Brief
 *       - variant="featured": ALWAYS visible (primary card = centerpiece)
 *       - variant="default":  fades in on hover desktop, expands on tap mobile
 *   • Metadata row (Evidence · Sources · Timeline · Status)
 *   • "Open Dossier →" CTA
 *       - variant="featured": always visible with red pill treatment
 *       - variant="default":  appears on hover / tap
 *
 * Interaction model:
 *   Desktop → hover reveals brief + CTA on secondary cards. Click navigates.
 *   Mobile  → first tap "expands" secondary cards; second tap navigates.
 *   Featured card is always expanded — no gating.
 */
import React, { useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileText, Link2, Clock, Camera } from "lucide-react";

const STATUS_LABELS = {
  // Canonical 5-value living-intelligence system
  active: "ACTIVE",
  under_review: "UNDER REVIEW",
  archived: "ARCHIVED",
  closed: "CLOSED",
  restricted: "RESTRICTED",
  // Legacy / synonyms — kept for backwards compatibility with older
  // rows that still emit these values.
  ongoing: "ACTIVE",
  published: "ACTIVE",
  monitored: "UNDER REVIEW",
  review: "UNDER REVIEW",
  draft: "DRAFT",
};

const STATUS_COLORS = {
  ACTIVE: "#3FB950",         // 🟢
  "UNDER REVIEW": "#E0A526", // 🟠
  ARCHIVED: "#B7B0A4",       // ⚪
  CLOSED: "#D92D20",         // 🔴
  RESTRICTED: "#3F3B36",     // ⚫
  DRAFT: "#7B7168",
  // Legacy label kept as an alias for callers that pre-normalized to
  // MONITORED before the 5-value system landed. Both render as amber.
  MONITORED: "#E0A526",
};

const _statusLabel = (raw) => {
  if (!raw) return "ACTIVE";
  const key = String(raw).toLowerCase();
  if (STATUS_LABELS[key]) return STATUS_LABELS[key];
  // Backwards-compat: server already returned an uppercase label
  // (e.g. "ACTIVE") — pass it through if it's a known status.
  const upper = String(raw).toUpperCase();
  if (STATUS_COLORS[upper]) return upper;
  return "ACTIVE";
};
const _statusColor = (label) => STATUS_COLORS[label] || "#3FB950";

/** Resolve the correct src for a card image. Backend serves images at
 *  /api/media/generated/... which is same-origin relative to the preview
 *  ingress. External URLs pass through unchanged. */
const _resolveImageUrl = (src) => {
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return src.startsWith("/") ? `${base}${src}` : src;
};

export const DossierCard = ({ dossier, variant = "default", eager = false }) => {
  const [tapped, setTapped] = useState(false);
  const [isHover, setHover] = useState(false);
  const nav = useNavigate();
  const cardRef = useRef(null);

  const isFeatured = variant === "featured";
  const status = _statusLabel(dossier.status);
  const statusColor = _statusColor(status);
  const brief = dossier.intelligence_brief || dossier.subtitle || "";
  const heroSrc = _resolveImageUrl(dossier.image_url || dossier.hero_image);

  const onCardClick = useCallback((e) => {
    // Featured cards navigate on first click — no gating.
    if (isFeatured) { nav(`/topic/${dossier.slug}`); return; }
    // Mobile: two-tap interaction for secondary cards. Desktop: single click.
    if (typeof window !== "undefined" && window.matchMedia("(hover: none)").matches) {
      if (!tapped) {
        e.preventDefault();
        setTapped(true);
        return;
      }
    }
    nav(`/topic/${dossier.slug}`);
  }, [isFeatured, tapped, nav, dossier.slug]);

  // Featured cards are always "revealed"; secondary cards reveal on hover/tap.
  const showReveal = isFeatured || isHover || tapped;

  return (
    <article
      ref={cardRef}
      onClick={onCardClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`dossier-card-${dossier.slug}`}
      data-variant={variant}
      className={`
        group relative overflow-hidden cursor-pointer
        border transition-all duration-300 ease-out
        flex flex-col
        ${isFeatured ? "min-h-[480px] md:min-h-[560px]" : "min-h-[340px] md:min-h-[380px]"}
        hover:-translate-y-1
      `}
      style={{
        borderColor: showReveal ? statusColor + "66" : "#2D2A26",
        background: "#0B0A0D",
        boxShadow: showReveal
          ? `0 30px 60px -18px ${statusColor}22, 0 0 0 1px ${statusColor}44 inset`
          : "0 8px 20px -8px rgba(0,0,0,0.6)",
      }}
    >
      {/* Image layer with duotone-ish overlay */}
      <div className="absolute inset-0">
        {heroSrc ? (
          <img
            src={heroSrc}
            alt=""
            loading={eager ? "eager" : "lazy"}
            onError={(e) => { e.target.style.display = "none"; }}
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            style={{ filter: "saturate(0.75) contrast(1.06)" }}
          />
        ) : (
          <FallbackVisual category={dossier.category} title={dossier.title} accent={statusColor} />
        )}
        {/* Dark gradient — always present, deepens on hover */}
        <div
          className="absolute inset-0 transition-opacity duration-300"
          style={{
            background: isFeatured
              ? "linear-gradient(180deg, rgba(11,10,13,0.15) 0%, rgba(11,10,13,0.55) 50%, rgba(11,10,13,0.96) 100%)"
              : "linear-gradient(180deg, rgba(11,10,13,0.35) 0%, rgba(11,10,13,0.55) 45%, rgba(11,10,13,0.95) 100%)",
          }}
        />
        {/* Vignette / grain hint */}
        <div
          className="absolute inset-0 pointer-events-none opacity-30 transition-opacity duration-500"
          style={{
            background:
              "radial-gradient(circle at 60% 40%, transparent 20%, rgba(0,0,0,0.55) 100%)",
          }}
        />
      </div>

      {/* Top row — category + status chips */}
      <div className="relative z-10 flex items-start justify-between p-4 md:p-5">
        <div className="flex items-center gap-2">
          {isFeatured && (
            <span
              data-testid="dossier-card-featured-badge"
              className="font-mono text-[9px] uppercase tracking-[0.35em] px-2 py-1"
              style={{
                background: "#D92D20",
                color: "#FFF",
                borderLeft: "2px solid #7A1811",
              }}
            >
              Featured
            </span>
          )}
          <span
            data-testid="dossier-card-category"
            className="font-mono text-[9px] uppercase tracking-[0.35em] px-2 py-1 border"
            style={{ borderColor: "#E4DFD3AA", color: "#E4DFD3", background: "rgba(0,0,0,0.35)" }}
          >
            {dossier.category || "CLASSIFIED"}
          </span>
        </div>
        <span
          data-testid="dossier-card-status"
          className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.35em] px-2 py-1"
          style={{ color: statusColor, background: "rgba(0,0,0,0.55)", borderLeft: `2px solid ${statusColor}` }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
          {status}
        </span>
      </div>

      {/* Bottom content */}
      <div className={`relative z-10 mt-auto p-4 md:p-6 flex flex-col gap-2 md:gap-3`}>
        <h3
          data-testid="dossier-card-title"
          className={`font-display text-[#E4DFD3] leading-[1.05] tracking-tight ${
            isFeatured ? "text-[28px] md:text-[42px]" : "text-[19px] md:text-[22px]"
          }`}
        >
          {dossier.title}
        </h3>

        {/* Intelligence Brief — clamped to 3 lines so hover-reveal never
            overruns the metadata / CTA rows beneath it. */}
        <p
          data-testid="dossier-card-brief"
          className={`text-[#B9B2A4] leading-relaxed transition-opacity duration-300 ${
            isFeatured
              ? "opacity-100 text-[14px] md:text-[15px] line-clamp-4"
              : (showReveal ? "opacity-100 text-[13px] md:text-[13.5px] line-clamp-3" : "opacity-0 max-h-0 overflow-hidden text-[13px]")
          }`}
        >
          {brief}
        </p>

        {/* Metadata row — always visible on featured, always on secondary too (design lock) */}
        <div
          data-testid="dossier-card-meta"
          className="flex items-center gap-3 md:gap-5 flex-wrap text-[10px] font-mono uppercase tracking-[0.25em] text-[#B9B2A4] pt-2 border-t"
          style={{ borderColor: "#2D2A26" }}
        >
          <span className="flex items-center gap-1.5" data-testid="dossier-card-evidence">
            <FileText size={11} className="text-[#D92D20]" />
            <span className="text-[#E4DFD3]">{dossier.evidence_count ?? "—"}</span>
            <span className="text-[#7B7168]">evidence</span>
          </span>
          <span className="flex items-center gap-1.5" data-testid="dossier-card-sources">
            <Link2 size={11} className="text-[#D92D20]" />
            <span className="text-[#E4DFD3]">{dossier.source_count ?? "—"}</span>
            <span className="text-[#7B7168]">sources</span>
          </span>
          {dossier.authenticated_image_count > 0 && (
            <span className="flex items-center gap-1.5"
                  data-testid="dossier-card-authenticated-images"
                  title={`${dossier.authenticated_image_count} authenticated images`}>
              <Camera size={11} className="text-[#7B7168]" aria-hidden="true" />
              <span className="text-[#E4DFD3]" aria-hidden="true">{dossier.authenticated_image_count}</span>
              <span className="sr-only">{dossier.authenticated_image_count} authenticated images</span>
            </span>
          )}
          {dossier.date_range && (
            <span className="flex items-center gap-1.5" data-testid="dossier-card-timeline">
              <Clock size={11} className="text-[#D92D20]" />
              <span className="text-[#E4DFD3]">{dossier.date_range}</span>
            </span>
          )}
          {/* Living-intelligence chips — hidden entirely when the
              underlying signal is empty, per the "never invent data"
              editorial rule. */}
          {dossier.reading_time_minutes > 0 && (
            <span className="flex items-center gap-1.5" data-testid="dossier-card-read-time">
              <Clock size={11} className="text-[#7B7168]" />
              <span className="text-[#E4DFD3]">{dossier.reading_time_minutes}m</span>
              <span className="text-[#7B7168]">read</span>
            </span>
          )}
          {dossier.connection_count > 0 && (
            <span className="flex items-center gap-1.5" data-testid="dossier-card-connections">
              <ShieldCheck size={11} className="text-[#7B7168]" />
              <span className="text-[#E4DFD3]">{dossier.connection_count}</span>
              <span className="text-[#7B7168]">connected</span>
            </span>
          )}
        </div>

        {/* CTA */}
        <div
          data-testid="dossier-card-cta-wrap"
          className={`transition-all duration-300 pt-1 ${
            isFeatured
              ? "opacity-100 max-h-12 mt-2"
              : (showReveal ? "opacity-100 max-h-10 mt-1" : "opacity-0 max-h-0 overflow-hidden")
          }`}
        >
          {isFeatured ? (
            <Link
              to={`/topic/${dossier.slug}`}
              onClick={(e) => e.stopPropagation()}
              data-testid="dossier-card-cta"
              className="inline-flex items-center gap-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.3em]
                         border-2 border-[#D92D20] text-[#E4DFD3] hover:bg-[#D92D20] transition-colors"
              style={{ background: "rgba(217,45,32,0.22)" }}
            >
              Open Dossier <ArrowRight size={13} />
            </Link>
          ) : (
            <Link
              to={`/topic/${dossier.slug}`}
              onClick={(e) => e.stopPropagation()}
              data-testid="dossier-card-cta"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#D92D20] hover:text-[#F87171]"
            >
              Open Dossier <ArrowRight size={13} />
            </Link>
          )}
        </div>
      </div>

      {/* Corner tick — subtle "authenticated" mark */}
      <div className="absolute top-3 right-3 opacity-40 group-hover:opacity-80 transition-opacity">
        <ShieldCheck size={10} className="text-[#E4DFD3]" />
      </div>
    </article>
  );
};

const CATEGORY_ACCENTS = {
  History: "#A68F5E",
  Politics: "#7C5B9C",
  Tech: "#3FB950",
  Science: "#4B9CD3",
  Health: "#C86D46",
  Conspiracy: "#D92D20",
  "Pop Culture": "#E0A526",
  Religion: "#9B7B4E",
};

const _accent = (cat) => CATEGORY_ACCENTS[cat] || "#D92D20";

/** Textured placeholder when a dossier lacks an image. Category-tinted radial
 *  gradient + a repeating diagonal grid + a large muted monogram. Feels
 *  premium and archival without requiring an external image. */
const FallbackVisual = ({ category, title, accent }) => {
  const initials = (title || "??").split(" ").slice(0, 3).map((w) => w[0]).join("");
  const catAccent = _accent(category);
  return (
    <div
      className="w-full h-full relative"
      style={{
        background: `
          radial-gradient(120% 90% at 20% 10%, ${catAccent}44 0%, transparent 55%),
          radial-gradient(80% 60% at 90% 90%, ${accent}22 0%, transparent 50%),
          repeating-linear-gradient(45deg, #191418 0px, #191418 2px, #0B0A0D 2px, #0B0A0D 10px)
        `,
      }}
    >
      {/* Fine grid overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(228,223,211,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(228,223,211,1) 1px, transparent 1px)
          `,
          backgroundSize: "28px 28px",
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="font-display text-[80px] md:text-[140px] opacity-[0.12] text-[#E4DFD3] leading-none select-none tracking-tight"
        >
          {initials.toUpperCase()}
        </span>
      </div>
      {/* Corner classification stamp */}
      <div className="absolute top-4 right-4 opacity-30 rotate-6">
        <div
          className="border-2 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.3em]"
          style={{ borderColor: catAccent, color: catAccent }}
        >
          CLASSIFIED
        </div>
      </div>
    </div>
  );
};

export default DossierCard;
